import React, { useMemo, useState } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
} from "recharts";
import { api } from "@/lib/api";
import { AlertTriangle, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * SkillsRadar
 * -----------
 * Chart + critical-gap alerts. For each required skill with coverage < 40,
 * shows a "Suggest question" button that calls the LLM screening endpoint.
 *
 * Coverage rules per skill:
 *   • Listed in candidate.skills   →  100
 *   • Appears in raw resume text   →   65
 *   • Otherwise                    →   15  (flagged as critical gap)
 */
const coverageFor = (skill, haveSet, rt) => {
  const key = (skill || "").toLowerCase();
  if (haveSet.has(key)) return 100;
  if (key && rt.includes(key)) return 65;
  return 15;
};

export const SkillsRadar = ({
  jobId,
  requiredSkills = [],
  candidateSkills = [],
  resumeText = "",
}) => {
  const req = requiredSkills.slice(0, 6);
  const haveSet = useMemo(
    () => new Set((candidateSkills || []).map((s) => (s || "").toLowerCase())),
    [candidateSkills]
  );
  const rt = (resumeText || "").toLowerCase();

  const data = useMemo(
    () =>
      req.map((skill) => ({
        skill: skill.length > 14 ? skill.slice(0, 12) + "…" : skill,
        fullSkill: skill,
        required: 100,
        coverage: coverageFor(skill, haveSet, rt),
      })),
    [req, haveSet, rt]
  );

  const gaps = data.filter((d) => d.coverage < 40).map((d) => d.fullSkill);

  if (req.length === 0) {
    return (
      <div className="text-xs text-slate-500 text-center py-6">
        No required skills defined for this role.
      </div>
    );
  }

  return (
    <div>
      <div className="w-full h-64" data-testid="skills-radar">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="72%">
            <PolarGrid stroke="rgba(255,255,255,0.10)" />
            <PolarAngleAxis
              dataKey="skill"
              tick={{ fill: "#CBD5E1", fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
            />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
            <Radar
              name="Required"
              dataKey="required"
              stroke="rgba(148,163,184,0.4)"
              fill="rgba(148,163,184,0.05)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Radar
              name="Candidate"
              dataKey="coverage"
              stroke="#06B6D4"
              fill="#06B6D4"
              fillOpacity={0.35}
              isAnimationActive={true}
            />
            <Tooltip
              contentStyle={{
                background: "#0B0F19",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                fontSize: 12,
                color: "#E2E8F0",
              }}
              labelStyle={{ color: "#94A3B8", fontFamily: "JetBrains Mono, monospace" }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {gaps.length > 0 && jobId && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="mono-label mb-3 text-[#F59E0B] flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> critical gaps · suggested screening
          </div>
          <div className="space-y-2">
            {gaps.map((skill) => (
              <GapItem key={skill} jobId={jobId} skill={skill} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GapItem = ({ jobId, skill }) => {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/match/screening-question", {
        job_id: jobId,
        skill,
      });
      setQ(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate question");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`gap-item-${skill.toLowerCase().replace(/\s+/g, "-")}`}
      className="border border-white/10 bg-[#0B0F19]/70 rounded-lg p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-200">
          <span
            className="text-[10px] px-2 py-0.5 rounded border border-[#F59E0B]/40 text-[#F59E0B] font-mono uppercase"
          >
            gap
          </span>
          <span className="font-medium">{skill}</span>
        </div>
        {!q && (
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid={`gap-suggest-${skill.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-xs text-[#06B6D4] hover:text-[#22D3EE] flex items-center gap-1 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> generating…
              </>
            ) : (
              <>
                <Lightbulb className="w-3 h-3" /> Suggest question
              </>
            )}
          </button>
        )}
      </div>
      {q && (
        <div className="mt-3 space-y-2 text-sm text-slate-300 leading-relaxed">
          <div className="flex gap-2">
            <span className="text-[#06B6D4] mt-0.5">Q.</span>
            <span>{q.question}</span>
          </div>
          {q.follow_up && (
            <div className="flex gap-2 text-xs text-slate-500">
              <span className="text-slate-500 mt-0.5">↳</span>
              <span>{q.follow_up}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
